import type { LookalikePersona, LookalikeSchema } from '@repo/contracts';

import type { RandomSource } from './prng';
import { gaussian, uniform, weightedPick } from './prng';

/** A continuous field is sampled uniformly across its range, then nudged by a
 *  little Gaussian jitter — a flat line of ages reads as generated; a
 *  population clustering loosely around a centre reads as real. Clamped back
 *  into range afterwards, since the tail of a Gaussian doesn't know about
 *  `min`/`max`. */
function sampleRange(
  random: RandomSource,
  range: { min: number; max: number },
): number {
  const span = range.max - range.min;
  const value =
    uniform(random, range.min, range.max) + gaussian(random) * (span * 0.08);
  return Math.min(range.max, Math.max(range.min, value));
}

/**
 * Draws one population from a schema — the whole of "generate N lookalikes."
 *
 * Every field is an independent draw. That's a deliberate simplification, not
 * an oversight: `LookalikeSchema` has no correlation/constraint rules between
 * pools (see the design discussion this shipped from — independent sampling
 * plus a *few* well-chosen correlations beats many chained ones, and chained
 * correlations were the harder, riskier case to get right first). A future
 * schema revision can add them without changing this function's shape.
 *
 * Deterministic in `seed` and `count` alone: same inputs, same population,
 * on the server or re-sampled optimistically in the browser after a weight
 * slider moves.
 */
export function sampleLookalikes(
  schema: LookalikeSchema,
  random: RandomSource,
  count: number,
): LookalikePersona[] {
  const lookalikes: LookalikePersona[] = [];
  for (let index = 0; index < count; index += 1) {
    lookalikes.push({
      age: Math.round(sampleRange(random, schema.ageRange)),
      brandLoyalty: weightedPick(random, schema.brandLoyalty),
      // Assigned by `assignClusters` once the whole population exists —
      // a single row can't know its own cluster in isolation.
      cluster: -1,
      companySize: weightedPick(random, schema.companySizes),
      experienceYears:
        Math.round(sampleRange(random, schema.experienceYearsRange) * 10) / 10,
      goal: weightedPick(random, schema.goals),
      id: `${index}`,
      index,
      jobTitle: weightedPick(random, schema.jobTitles),
      location: weightedPick(random, schema.locations),
      painPoint: weightedPick(random, schema.painPoints),
      techSavviness: weightedPick(random, schema.techSavviness),
    });
  }
  return lookalikes;
}
